import { useCallback, useEffect, useState } from "react";
import { useQuery } from "react-query";

import styled from "styled-components";
import { FLEX } from "styles/flex";
import { InfoBoxStyled } from "../SubscriptionStyled";
import { COLORS, tierColor } from "styles/colors";
import {
  DISCOUNT_TIER,
  GetServicePricingResponse,
  ServicePricingResponse,
  SUBSCRIPTION_TYPE,
  SubscriptionBaseOutletContext,
  SubscriptionDiscountTier,
  SubscriptionService,
} from "api/interfaces/subscription.interface";
import {
  TERM_DISCOUNT_YEAR,
  YearPaymentPrice,
} from "api/interfaces/planInterface.interface";
import { ReactComponent as TIER } from "assets/icons/tier.svg";
import { calcRem } from "styles/theme";
import { InputNumber } from "antd";
import BigButton from "components/atoms/buttons/BigButton";
import { getDiscountService, putDiscountService } from "api/subscriptionAPI";
import { CustomError } from "api/interfaces/commonInterface.interface";
import useStatusMutation from "hook/useStatusMutation";
import useApiError from "hook/useApiError";
import { Text } from "components/atoms/text/Text";
import { changeServiceName, useKeyType } from "hook/subscription/useKeyType";
import * as mediaQuery from "components/MediaQuery";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { useOutletContext } from "react-router-dom";

type Props = {};

const initialDiscountTier = [
  // {
  //   id: 1,
  //   name: DISCOUNT_TIER.PLATINUM,
  //   discount: 0,
  // },
  {
    id: 2,
    name: DISCOUNT_TIER.GOLD,
    discount: 54,
  },
  {
    id: 3,
    name: DISCOUNT_TIER.SILVER,
    discount: 49,
  },
  {
    id: 4,
    name: DISCOUNT_TIER.AUTHORIZED,
    discount: 43,
  },
];

const initialService = [
  {
    id: 1,
    name: changeServiceName(SUBSCRIPTION_TYPE.STANDARD),
    type: SUBSCRIPTION_TYPE.STANDARD,
    msrp: 3,
  },
  {
    id: 2,
    name: changeServiceName(SUBSCRIPTION_TYPE.STORAGE),
    type: SUBSCRIPTION_TYPE.STORAGE,
    msrp: 20,
  },
];

const mapDiscountTierData = (
  discount?: SubscriptionDiscountTier[]
): SubscriptionDiscountTier[] =>
  initialDiscountTier.map(
    (initial) => discount?.find((item) => item.name === initial.name) ?? initial
  );

const TERM_DISCOUNT_YEAR_NUM: Record<TERM_DISCOUNT_YEAR, number> = {
  [TERM_DISCOUNT_YEAR.YEAR1]: 1,
  [TERM_DISCOUNT_YEAR.YEAR2]: 2,
  [TERM_DISCOUNT_YEAR.YEAR3]: 3,
};

const initialTermDiscount: YearPaymentPrice[] = [
  { id: 1, name: TERM_DISCOUNT_YEAR.YEAR1, discount: 0 },
  { id: 2, name: TERM_DISCOUNT_YEAR.YEAR2, discount: 0 },
  { id: 3, name: TERM_DISCOUNT_YEAR.YEAR3, discount: 0 },
];

const mapTermDiscountData = (year?: YearPaymentPrice[]): YearPaymentPrice[] =>
  initialTermDiscount.map(
    (initial) => year?.find((item) => item.name === initial.name) ?? initial
  );

export default function ServicePricing(props: Props): JSX.Element {
  const { handleErrorNotify, notifySuccess, lang } = useApiError();
  const { durationMonthOptions } = useKeyType();
  const { userPermission }: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { setButtons } = useOutletContext<SubscriptionBaseOutletContext>();

  const [discountTierData, setDiscountTier] = useState<
    SubscriptionDiscountTier[]
  >([...initialDiscountTier]);
  const [serviceData, setServiceData] = useState<SubscriptionService[]>([
    ...initialService,
  ]);
  const [termDiscountData, setTermDiscount] = useState<YearPaymentPrice[]>([
    ...initialTermDiscount,
  ]);
  const [subscriptionInterval, setSubscriptionInterval] = useState<number>(12);

  const { isFetching } = useQuery(
    ["getDiscountService"],
    () => getDiscountService({}),
    {
      refetchOnWindowFocus: false,
      select: (res: GetServicePricingResponse) => {
        if (res.error === 0 && res.result) {
          return {
            ...res.result,
            discount: mapDiscountTierData(res.result.discount),
            year: mapTermDiscountData(res.result.year),
          };
        }
      },
      onSuccess: (res: ServicePricingResponse) => {
        if (res.discount) {
          setDiscountTier([...res.discount]);
        }
        if (res.service) {
          setServiceData([...res.service]);
        }
        if (res.year) {
          setTermDiscount([...res.year]);
        }
        if (res.subscriptionInterval) {
          setSubscriptionInterval(res.subscriptionInterval);
        }
      },
      onError: (err: CustomError) => {
        handleErrorNotify(err, lang.label.subscription.service.get.fail);
      },
    }
  );

  const mutationUpdate = useStatusMutation(putDiscountService, {
    onSuccess: () => {
      notifySuccess(lang.label.subscription.service.update.success);
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.subscription.service.update.fail);
    },
  });

  const onChangeDiscountTier = useCallback(
    (name: DISCOUNT_TIER, value: number) => {
      if (value > 100 || value < 0) return;
      setDiscountTier((prev) =>
        prev.map((item) =>
          item.name === name ? { ...item, discount: value } : item
        )
      );
    },
    [setDiscountTier]
  );

  const onChangeService = useCallback(
    (type: SUBSCRIPTION_TYPE, value: number) => {
      setServiceData((prev) =>
        prev.map((item) =>
          item.type === type ? { ...item, msrp: value } : item
        )
      );
    },
    [setServiceData]
  );

  const onChangeTermDiscount = useCallback(
    (name: TERM_DISCOUNT_YEAR, value: number) => {
      if (value > 100 || value < 0) return;
      setTermDiscount((prev) =>
        prev.map((item) =>
          item.name === name ? { ...item, discount: value } : item
        )
      );
    },
    [setTermDiscount]
  );

  const onChangeInterval = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSubscriptionInterval(Number(e.target.value));
    },
    [setSubscriptionInterval]
  );

  const onMutateUpdate = () => {
    if (!mutationUpdate.idle) return;
    mutationUpdate.mutate({
      payload: {
        discount: discountTierData,
        service: serviceData,
        year: termDiscountData,
        subscriptionInterval,
      },
    });
  };
  useEffect(() => {
    setButtons(null);
  }, [setButtons]);

  const isAllDisabled = !userPermission.serviceAndPricingUpdate;

  if (isFetching) {
    return <></>;
  }
  return (
    <PageInner>
      <ItemWrapper>
        <Item>
          <Text bold fontSize={18} className="title">
            Discount Tiers
          </Text>
          <div>
            <TierItem>
              <div className="title ">Tier</div>
              <div className="value bold">Discount</div>
            </TierItem>
            {discountTierData.map((tier: SubscriptionDiscountTier) => {
              return (
                <TierItem tier={tier.name} key={tier.name}>
                  <div className="title tier-title">
                    <TIER />
                    {tier.name.toUpperCase()}
                  </div>
                  <span className="value unit">
                    <InputNumber
                      disabled={isAllDisabled}
                      value={tier.discount}
                      min={0}
                      max={100}
                      onChange={(value) => {
                        if (typeof value === "number" && value) {
                          onChangeDiscountTier(tier.name, value);
                        }
                      }}
                    />
                    <span>%</span>
                  </span>
                </TierItem>
              );
            })}
          </div>
        </Item>
        <Item>
          <Text bold fontSize={18} className="title">
            Services
          </Text>
          <div>
            <TierItem>
              <div className="title ">Type</div>
              <div className="value bold ">Price</div>
            </TierItem>
            {serviceData.map((service: SubscriptionService) => {
              return (
                <TierItem key={service.type}>
                  <div className="title service ">
                    <span className="bold">
                      {changeServiceName(service.type)}
                    </span>
                    <span>
                      {service.type === SUBSCRIPTION_TYPE.STORAGE
                        ? "Capacity (TB)"
                        : "Channel"}{" "}
                      <span className="bold">/ MSRP</span>
                    </span>
                  </div>
                  <span className="value unit">
                    <span>$</span>
                    <InputNumber
                      disabled={isAllDisabled}
                      stringMode={false}
                      value={service.msrp}
                      onChange={(value) => {
                        if (typeof value === "number" && value) {
                          onChangeService(service.type, value);
                        }
                      }}
                    />
                  </span>
                </TierItem>
              );
            })}
          </div>
        </Item>
        <Item>
          <div className="title-row">
            <Text bold fontSize={18} className="title">
              Multi-Year Prepay Discount
            </Text>
          </div>

          <div>
            <TierItem>
              <div className="title ">Term</div>
              <div className="value bold">Discount</div>
            </TierItem>
            {termDiscountData.map((term: YearPaymentPrice) => {
              const years = TERM_DISCOUNT_YEAR_NUM[term.name];
              return (
                <TierItem key={term.name}>
                  <div className="title service ">
                    <span className="bold">
                      {years} {years === 1 ? "Year" : "Years"}
                    </span>
                  </div>
                  <span className="value unit">
                    <InputNumber
                      disabled={isAllDisabled}
                      value={term.discount}
                      min={0}
                      max={100}
                      onChange={(value) => {
                        if (
                          typeof value === "number" &&
                          Number.isFinite(value)
                        ) {
                          onChangeTermDiscount(term.name, value);
                        }
                      }}
                    />
                    <span>%</span>
                  </span>
                </TierItem>
              );
            })}
          </div>
          <PanelNote>
            Applies only to accounts with Billing Type set to Usage Based Plan.
          </PanelNote>
        </Item>
      </ItemWrapper>
      <Bottom>
        <div className="interval-wrapper">
          <Text bold fontSize={20}>
            Default Subscription Interval
          </Text>
          <div className="option">
            {durationMonthOptions(SUBSCRIPTION_TYPE.STORAGE, false).map(
              (option, index) => {
                return (
                  <label key={`interval-${index}`}>
                    <input
                      type="radio"
                      value={option.value}
                      checked={option.value === String(subscriptionInterval)}
                      onChange={onChangeInterval}
                      disabled={isAllDisabled}
                    />
                    {option.label}
                  </label>
                );
              }
            )}
          </div>
        </div>
        <BigButton
          label="Save Changes"
          onClickBtn={() => onMutateUpdate()}
          disabled={!mutationUpdate.idle || isAllDisabled}
        />
      </Bottom>
    </PageInner>
  );
}

const PageInner = styled.div`
  width: 100%;

  @media (max-width: 1440px) {
    width: 100%;
    max-width: 100%;
    padding-top: 20px;
  }

  .bold {
    font-weight: 500;
  }
`;

const ItemWrapper = styled.ul`
  ${FLEX.FlexStartStart};
  align-items: stretch;
  gap: 16px;
  width: 100%;

  ${mediaQuery.isTablet} {
    flex-direction: column;
  }
`;

const Item = styled(InfoBoxStyled.Box)`
  flex: 1;
  color: ${({ theme }) => theme.COLORS.WHITE};
  padding: 20px;

  .title-row {
    ${FLEX.FlexStartCenter};
    gap: 8px;
    flex-wrap: wrap;
  }

  ${mediaQuery.isMobile} {
    width: 100%;
  }
`;

const PanelNote = styled.p`
  margin-top: 6px;
  color: ${({ theme }) => theme.COLORS.BUTTONS3};
  font-size: ${calcRem(14)};
  line-height: 1.4;
`;

const TierItem = styled.li<{ tier?: DISCOUNT_TIER }>`
  border-bottom: 1px solid ${COLORS.BORDER};
  padding: 8px 0;
  width: 100%;
  flex: 1;
  ${FLEX.FlexBetweenCenter};
  gap: 20px;
  align-items: stretch;
  font-size: ${calcRem(14)};

  .title {
    flex: 3;
    padding-bottom: 0;
  }
  .title.service {
    ${FLEX.FlexBetweenCenter};
    font-size: ${calcRem(16)};
    font-weight: normal;
    flex-wrap: wrap;
    row-gap: 2px;
  }

  .tier-title {
    font-size: ${calcRem(18)};
    ${FLEX.FlexStartCenter};
    gap: 4px;

    ${({ tier, theme }) =>
      tier &&
      `
      color: ${tierColor(tier, theme)};
    `}

    svg {
      width: 18px;
    }

    * {
      ${({ tier, theme }) =>
        tier &&
        `
      color: ${tierColor(tier, theme)};
    `}
    }
  }
  .value {
    ${FLEX.FlexStartCenter};
    width: 90px;
  }

  .value.unit {
    gap: 8px;
    font-size: ${calcRem(18)};
  }
  .ant-input-number {
    width: 60px !important;
  }
`;

const Bottom = styled.div`
  color: ${({ theme }) => theme.COLORS.WHITE};
  ${FLEX.FlexBetweenCenter};
  width: 100%;
  padding: 20px 0;

  ${mediaQuery.isMobile} {
    align-items: flex-start;
    flex-direction: column;

    button {
      margin-top: 30px;
    }
  }

  .interval-wrapper {
    ${FLEX.FlexStartCenter};
    gap: 20px;

    .option {
      ${FLEX.FlexStartCenter};
      gap: 10px;
    }

    ${mediaQuery.isMobile} {
      width: 100%;
      flex-direction: column;
      gap: 10px;
    }
  }
`;
